'use client';

import { Fragment, useState } from 'react';
import type { ArchitectureState, DotStatus, SourceId } from '@/lib/architecture-state';

const DOT: Record<DotStatus, string> = {
  ok: 'bg-emerald-400 shadow-[0_0_6px_rgba(74,222,128,0.55)]',
  warn: 'bg-amber-300 shadow-[0_0_6px_rgba(252,211,77,0.5)]',
  fail: 'bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.55)]',
  na: 'bg-zinc-700',
};

const SOURCE_LABEL: Record<SourceId, string> = {
  sapi: 'Bloomberg SAPI',
  fmp: 'FMP',
  sec: 'SEC EDGAR',
  computed: 'Computed',
};

const SOURCE_ORDER: SourceId[] = ['sapi', 'fmp', 'sec', 'computed'];

function fmtCount(n: number | null): string {
  if (n == null) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wd = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][dt.getUTCDay()];
  return `${wd} ${m}/${d}`;
}

async function triggerRefresh(pullId: string): Promise<void> {
  try {
    const res = await fetch(`/api/pipeline-refresh?id=${encodeURIComponent(pullId)}`, {
      method: 'POST',
    });
    if (!res.ok) {
      // Silent on UI; cron status will surface failure
    }
  } catch {
    // ignore network errors -- status row will recolor on next poll
  }
}

interface Props {
  windowDates: string[];
  rows: ArchitectureState['pullTimeline'];
}

export function SourcesTimeline({ windowDates, rows }: Props): React.JSX.Element {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">Sources & Pulls</div>
        <div className="mt-3 text-[11px] text-white/40">No pulls in registry.</div>
      </div>
    );
  }

  // group by source
  const grouped: Record<SourceId, typeof rows> = {
    sapi: [],
    fmp: [],
    sec: [],
    computed: [],
  };
  for (const r of rows) {
    if (r.source in grouped) grouped[r.source].push(r);
  }

  return (
    <div className="rounded-md border border-zinc-800 bg-[#0a0e14] p-4">
      <div className="flex items-baseline justify-between">
        <div className="text-[12px] uppercase tracking-wider text-[#c9a84c]">Sources & Pulls</div>
        <div className="text-[10px] text-white/40">
          {rows.length} pulls × 5 days · click row to expand · hover for refresh
        </div>
      </div>

      <div className="mt-3 space-y-3">
        {SOURCE_ORDER.map((src) => {
          const group = grouped[src];
          if (group.length === 0) return null;
          const isCollapsed = collapsed[src];
          const failsToday = group.filter((g) => g.days[g.days.length - 1]?.status === 'fail').length;
          const warnsToday = group.filter((g) => g.days[g.days.length - 1]?.status === 'warn').length;
          return (
            <div key={src} className="rounded border border-zinc-800/70 bg-zinc-950/40">
              <button
                type="button"
                onClick={() => setCollapsed((p) => ({ ...p, [src]: !p[src] }))}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-900/40"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-white/40">{isCollapsed ? '▸' : '▾'}</span>
                  <span className="text-[11px] uppercase tracking-wider text-white/80">
                    {SOURCE_LABEL[src]}
                  </span>
                  <span className="text-[10px] text-white/40">{group.length} pulls</span>
                </div>
                <div className="flex items-center gap-3 text-[10px]">
                  {failsToday > 0 && <span className="text-red-300">{failsToday} fail</span>}
                  {warnsToday > 0 && <span className="text-amber-300">{warnsToday} warn</span>}
                  {failsToday === 0 && warnsToday === 0 && (
                    <span className="text-emerald-300">all green</span>
                  )}
                </div>
              </button>

              {!isCollapsed && (
                <div className="border-t border-zinc-900">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-[9px] uppercase tracking-wider text-white/40">
                        <th className="text-left font-normal py-1.5 pl-3 pr-2 w-[300px]">Pull</th>
                        {windowDates.map((d) => (
                          <th
                            key={d}
                            className="text-center font-normal py-1.5 px-1 tabular-nums"
                          >
                            {shortDate(d)}
                          </th>
                        ))}
                        <th className="text-right font-normal py-1.5 px-3 w-[100px]">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.map((row) => {
                        const isExpanded = expandedRow === row.id;
                        return (
                          <Fragment key={row.id}>
                            <tr
                              className="border-t border-zinc-900 group hover:bg-zinc-900/30 cursor-pointer"
                              onClick={() =>
                                setExpandedRow((prev) => (prev === row.id ? null : row.id))
                              }
                            >
                              <td className="py-1.5 pl-3 pr-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] text-white/40">
                                    {isExpanded ? '▾' : '▸'}
                                  </span>
                                  <span className="text-[11px] text-white/85">{row.id}</span>
                                  {row.scoringPath && (
                                    <span className="text-[8px] uppercase tracking-wider text-[#c9a84c] border border-[#c9a84c]/40 px-1 rounded">
                                      scoring
                                    </span>
                                  )}
                                </div>
                                <div className="text-[9px] text-white/40 ml-4 mt-0.5">
                                  {row.label}
                                </div>
                              </td>
                              {row.days.map((day) => (
                                <td key={day.date} className="py-1.5 px-1 text-center">
                                  <div className="flex flex-col items-center gap-0.5">
                                    <span
                                      className={`inline-block w-2 h-2 rounded-full ${DOT[day.status]}`}
                                      aria-hidden
                                    />
                                    <span className="text-[8px] tabular-nums text-white/50">
                                      {fmtCount(day.count)}
                                    </span>
                                  </div>
                                </td>
                              ))}
                              <td className="py-1.5 px-3 text-right">
                                {row.refreshCommand ? (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void triggerRefresh(row.id);
                                    }}
                                    className="text-[9px] uppercase tracking-wider text-white/50 hover:text-[#c9a84c] hover:underline"
                                  >
                                    refresh
                                  </button>
                                ) : (
                                  <span className="text-[9px] text-white/20">—</span>
                                )}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr className="bg-zinc-950/60 border-t border-zinc-900">
                                <td
                                  colSpan={1 + windowDates.length + 1}
                                  className="px-4 py-3 text-[10px] text-white/70 space-y-1"
                                >
                                  {row.cadence && (
                                    <div>
                                      <span className="text-white/40 uppercase tracking-wider">
                                        cadence:{' '}
                                      </span>
                                      {row.cadence}
                                    </div>
                                  )}
                                  {row.refreshCommand && (
                                    <div className="font-mono text-[10px] text-white/60 break-all">
                                      <span className="text-white/40 uppercase tracking-wider not-italic">
                                        cmd:{' '}
                                      </span>
                                      {row.refreshCommand}
                                    </div>
                                  )}
                                  {row.refreshNote && (
                                    <div className="text-white/60">
                                      <span className="text-white/40 uppercase tracking-wider">
                                        note:{' '}
                                      </span>
                                      {row.refreshNote}
                                    </div>
                                  )}
                                  {row.dependsOn && row.dependsOn.length > 0 && (
                                    <div>
                                      <span className="text-white/40 uppercase tracking-wider">
                                        depends on:{' '}
                                      </span>
                                      {row.dependsOn.join(', ')}
                                    </div>
                                  )}
                                  {row.tailPreview && (
                                    <div className="mt-2">
                                      <div className="text-[9px] uppercase tracking-wider text-white/40 mb-1">
                                        tail preview (today)
                                      </div>
                                      <pre className="text-[10px] text-white/60 whitespace-pre-wrap font-mono max-h-32 overflow-y-auto">
                                        {row.tailPreview}
                                      </pre>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
